import React, { useState, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Wifi, Monitor, ChevronDown, ChevronUp } from 'lucide-react';

const TripCard = ({ trip, onSelect, passengerCount = 1 }) => {
  const { t } = useLanguage();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const isLounge = trip.type === 'LUX Lounge';

  // Deterministic seat counts based on trip id (synced with BusSeatMap)
  // Standard bus: 42 actual seats, Relax 3-12 = 10, Standard = 32
  // Lounge bus: 32 actual seats, Relax 3-12 = 10, Standard = 22
  const totalStandard = isLounge ? 22 : 32;
  const totalRelax = 10;
  const seatSeed = useMemo(() => {
    let h = 0;
    const s = trip.id || '';
    for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; }
    return Math.abs(h);
  }, [trip.id]);
  const standardSeats = useMemo(() => Math.max(1, totalStandard - (seatSeed % Math.floor(totalStandard * 0.85))), [totalStandard, seatSeed]);
  const relaxSeats = useMemo(() => Math.max(1, totalRelax - ((seatSeed >> 8) % Math.floor(totalRelax * 0.8))), [totalRelax, seatSeed]);

  const passLabel = passengerCount === 1
    ? t('trip.passenger')
    : (passengerCount < 5 ? t('trip.passengers2') : t('trip.passengers5'));

  const BusTag = () => (
    <div className={`text-[9px] md:text-[10px] font-bold uppercase tracking-[0.08em] px-4 py-[5px] rounded-br-lg inline-block ${
      isLounge
        ? 'bg-gradient-to-r from-[#b8960c] to-[#d4af37] text-white'
        : 'bg-[#484c55] text-white'
    }`}>
      {isLounge ? t('trip.busLounge') : t('trip.busExpress')}
    </div>
  );

  const BusLogo = () => isLounge ? (
    <span className="text-[11px] font-bold whitespace-nowrap">
      <span className="text-[#8a7000]">LUX</span>
      <span className="text-[#8a7000]/60 italic text-[10px] ml-0.5">Lounge</span>
    </span>
  ) : (
    <span className="text-[11px] font-bold whitespace-nowrap">
      <span className="text-[#c30909]">LUX</span>
      <span className="text-[#555] italic text-[10px] ml-0.5">Express</span>
    </span>
  );

  const RouteLine = () => (
    <div className="w-full flex items-center">
      <div className="w-2 h-2 rounded-full bg-[#484c55]/40 flex-shrink-0" />
      <div className="flex-1 h-px bg-[#484c55]/20 mx-0.5" />
      <div className="w-3.5 h-3.5 rounded-full bg-white border-[1.5px] border-[#484c55]/20 flex items-center justify-center flex-shrink-0">
        <div className="w-1.5 h-1.5 rounded-full bg-[#484c55]/20" />
      </div>
      <div className="flex-1 h-px bg-[#484c55]/20 mx-0.5" />
      <div className="w-2 h-2 rounded-full bg-[#484c55]/40 flex-shrink-0" />
    </div>
  );

  const BottomBar = () => (
    <div className="px-4 md:px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-xs text-[#777]">
      <div className="flex items-center gap-4">
        <span className="font-medium">{trip.duration}</span>
        <span className="text-[#c30909] font-semibold">{t('trip.direct')}</span>
      </div>
      <button type="button" onClick={() => setDetailsOpen(!detailsOpen)}
        className="text-[#555] hover:text-[#484c55] transition-colors flex items-center gap-1 font-medium">
        {detailsOpen ? (t('trip.hideDetails')) : (t('trip.details'))}
        {detailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
    </div>
  );

  const Details = () => detailsOpen ? (
    <div className="px-4 md:px-5 py-4 border-t border-gray-100 bg-[#fafafa]">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-start gap-3">
            <div className="flex flex-col items-center mt-0.5">
              <div className="w-2.5 h-2.5 rounded-full border-2 border-[#484c55]/30" />
              <div className="w-px h-10 bg-[#484c55]/15" />
              <div className="w-2.5 h-2.5 rounded-full bg-[#484c55]/50" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold">{trip.departureTime}</span>
                <span className="text-sm text-[#555]">{trip.from}</span>
              </div>
              <div className="h-6" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{trip.arrivalTime}</span>
                <span className="text-sm text-[#555]">{trip.to}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-start gap-2.5">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold text-[#484c55]">LUX</span>
            <span className="text-sm text-[#777] italic">Express</span>
          </div>
          <p className="text-xs text-[#484c55]/40">Lux Express Estonia AS</p>
          <div className="flex items-center gap-2 mt-1">
            {[{ label: '☕' }, { label: 'A/C' }, { Comp: Wifi }, { Comp: Monitor }, { label: 'WC' }].map((item, i) => (
              <div key={i} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[#484c55]/40">
                {item.Comp ? <item.Comp size={14} /> : <span className="text-[10px] font-semibold">{item.label}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
      {/* ===== DESKTOP ===== */}
      <div className="hidden md:block">
        <BusTag />
        <div className="px-5 py-4">
          <div className="flex items-center">
            {/* Times + route */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="flex-shrink-0 min-w-[60px]">
                <p className="text-[26px] font-bold leading-none">{trip.departureTime}</p>
                <p className="text-[9px] text-[#777] uppercase mt-1 leading-tight font-medium truncate max-w-[120px]">{trip.from}</p>
              </div>
              <div className="flex-1 flex flex-col items-center px-2 min-w-[70px]">
                <div className="mb-1.5"><BusLogo /></div>
                <RouteLine />
              </div>
              <div className="flex-shrink-0 text-right min-w-[60px]">
                <p className="text-[26px] font-bold leading-none">{trip.arrivalTime}</p>
                <p className="text-[9px] text-[#777] uppercase mt-1 leading-tight font-medium truncate max-w-[120px] ml-auto">{trip.to}</p>
              </div>
            </div>

            {/* Seats + Price */}
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-gray-100 flex-shrink-0">
              <div className="hidden lg:flex flex-col text-[11px] text-[#777] gap-0.5">
                <span>{standardSeats} {t('seat.standard')}</span>
                <span>{relaxSeats} {t('seat.relax')}</span>
              </div>
              <div className="flex flex-col items-center">
                <button onClick={() => onSelect(trip, isLounge)}
                  className="flex items-stretch rounded-md overflow-hidden bg-[#5a5e67] hover:bg-[#4a4e57] transition-colors">
                  <div className="text-white font-bold text-[13px] px-3 py-1.5 text-center min-w-[50px] flex items-center justify-center">
                    {trip.price.toFixed(0)} €
                  </div>
                  <div className="w-px bg-white/25 flex-shrink-0" />
                  <div className="text-white font-bold text-[13px] px-3 py-1.5 uppercase tracking-wide flex items-center justify-center">
                    {t('trip.buy')}
                  </div>
                </button>
                <span className="text-[11px] text-[#484c55]/40 mt-1.5">{passengerCount} {passLabel}</span>
              </div>
            </div>
          </div>
        </div>
        <BottomBar />
        <Details />
      </div>

      {/* ===== MOBILE ===== */}
      <div className="md:hidden">
        <BusTag />

        {/* Times + route */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex-shrink-0">
              <p className="text-[22px] font-bold leading-none">{trip.departureTime}</p>
              <p className="text-[9px] text-[#777] uppercase mt-1 leading-tight font-medium">{trip.from}</p>
            </div>
            <div className="flex-1 flex flex-col items-center px-1 min-w-[60px]">
              <div className="mb-1.5"><BusLogo /></div>
              <RouteLine />
            </div>
            <div className="flex-shrink-0 text-right">
              <p className="text-[22px] font-bold leading-none">{trip.arrivalTime}</p>
              <p className="text-[9px] text-[#777] uppercase mt-1 leading-tight font-medium">{trip.to}</p>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <BottomBar />

        {/* Dashed separator */}
        <div className="mx-4 border-t border-dashed border-gray-200" />

        {/* Seats centered */}
        <div className="flex items-center justify-center gap-5 py-2.5 text-[12px] text-[#777]">
          <span>{standardSeats} {t('seat.standard')}</span>
          <span>{relaxSeats} {t('seat.relax')}</span>
        </div>

        {/* Price + Buy */}
        <div className="flex justify-center pb-2 px-8">
          <button onClick={() => onSelect(trip, isLounge)}
            className="flex items-stretch rounded-lg overflow-hidden bg-[#5a5e67] hover:bg-[#4a4e57] transition-colors w-full max-w-[260px]">
            <div className="w-[38%] text-white font-bold text-[15px] py-2.5 text-center flex items-center justify-center">
              {trip.price.toFixed(0)} €
            </div>
            <div className="w-px bg-white/25 flex-shrink-0" />
            <div className="w-[62%] text-white font-bold text-[15px] py-2.5 text-center uppercase tracking-wider flex items-center justify-center">
              {t('trip.buy')}
            </div>
          </button>
        </div>

        {/* Passenger count */}
        <p className="text-center text-[11px] text-[#484c55]/40 pb-3">
          {passengerCount} {passLabel}
        </p>

        <Details />
      </div>
    </div>
  );
};

export default TripCard;
